import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FileText, Plus, Trash2, Download, Euro, Clock, 
  Calendar, Tractor, Building, Search, Filter, X
} from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TarifsETAPage() {
  const { activeSociete } = useWorkspace();
  const activeSocieteId = activeSociete?.id;
  
  const [activeTab, setActiveTab] = useState('grilles');
  const [grilles, setGrilles] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [clients, setClients] = useState([]);
  const [parcelles, setParcelles] = useState([]);
  const [equipe, setEquipe] = useState([]);
  const [operations, setOperations] = useState([]);
  const [materiels, setMateriels] = useState([]);
  const [selectedMois, setSelectedMois] = useState(new Date().getMonth() + 1);
  const [selectedAnnee, setSelectedAnnee] = useState(new Date().getFullYear());
  const [recapGlobal, setRecapGlobal] = useState(null);
  const [recapData, setRecapData] = useState(null);

  // États recherche grilles existantes
  const [searchNom, setSearchNom] = useState('');
  const [searchActivite, setSearchActivite] = useState('');
  const [searchStatut, setSearchStatut] = useState('tous'); // tous, avec_devis, sans_devis

  // États formulaire nouvelle grille
  const [showNewGrille, setShowNewGrille] = useState(false);
  const [grilleClientId, setGrilleClientId] = useState('');
  const [grilleAnnee, setGrilleAnnee] = useState(new Date().getFullYear());
  const [grilleLignes, setGrilleLignes] = useState([]);
  const [grilleNotes, setGrilleNotes] = useState('');
  const [grilleSearch, setGrilleSearch] = useState('');
  const [newOperation, setNewOperation] = useState('');
  const [newMateriel, setNewMateriel] = useState('');
  const [newTarif, setNewTarif] = useState('');
  const [newUnite, setNewUnite] = useState('ha');

  // États formulaire nouvelle intervention
  const [showNewIntervention, setShowNewIntervention] = useState(false);
  const [intClientId, setIntClientId] = useState('');
  const [intSearch, setIntSearch] = useState('');
  const [intParcelleIds, setIntParcelleIds] = useState([]);
  const [intOperation, setIntOperation] = useState('');
  const [intMateriel, setIntMateriel] = useState('');
  const [intDate, setIntDate] = useState(new Date().toISOString().split('T')[0]);
  const [intHeures, setIntHeures] = useState('');
  const [intQuantite, setIntQuantite] = useState('');
  const [intEmployeId, setIntEmployeId] = useState('');
  const [intCommentaire, setIntCommentaire] = useState('');

  const getHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'X-Societe-Id': activeSocieteId || ''
  });

  // Chargement des données
  useEffect(() => {
    if (activeSocieteId) {
      loadGrilles();
      loadClients();
      loadParcelles();
      loadEquipe();
      loadOperations();
    }
  }, [activeSocieteId]);

  useEffect(() => {
    if (activeTab === 'interventions') {
      loadInterventions();
    } else if (activeTab === 'recap') {
      loadRecapGlobal();
    }
  }, [activeTab, selectedMois, selectedAnnee, activeSocieteId]);

  const loadGrilles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tarifs-eta/grilles?annee=${selectedAnnee}`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setGrilles(data.grilles || []);
      }
    } catch (error) {
      console.error('Erreur chargement grilles:', error);
    }
  };

  const loadClients = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tarifs-eta/clients`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients || []);
      }
    } catch (error) {
      console.error('Erreur chargement clients:', error);
    }
  };

  const loadParcelles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/parcellaire/parcelles`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setParcelles(data.parcelles || []);
      }
    } catch (error) {
      console.error('Erreur chargement parcelles:', error);
    }
  };

  const loadEquipe = async () => {
    try {
      const res = await fetch(`${API_URL}/api/team/members`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setEquipe(data.members || []);
      }
    } catch (error) {
      console.error('Erreur chargement équipe:', error);
    }
  };

  const loadOperations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tarifs-eta/operations`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setOperations(data.operations || []);
        setMateriels(data.materiels || []);
      }
    } catch (error) {
      console.error('Erreur chargement opérations:', error);
    }
  };

  const loadInterventions = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/tarifs-eta/interventions?annee=${selectedAnnee}&mois=${selectedMois}&statut=a_facturer`,
        { headers: getHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setInterventions(data.interventions || []);
      }
    } catch (error) {
      console.error('Erreur chargement interventions:', error);
    }
  };

  const loadRecapGlobal = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/tarifs-eta/recapitulatif-global?annee=${selectedAnnee}&mois=${selectedMois}`,
        { headers: getHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setRecapGlobal(data);
      }
    } catch (error) {
      console.error('Erreur chargement récap:', error);
    }
  };

  const loadRecapClient = async (clientId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/tarifs-eta/recapitulatif/${clientId}?annee=${selectedAnnee}&mois=${selectedMois}`,
        { headers: getHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setRecapData(data);
      }
    } catch (error) {
      console.error('Erreur chargement récap client:', error);
    }
  };

  const downloadFacture = async (clientId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/tarifs-eta/facture-pdf/${clientId}?annee=${selectedAnnee}&mois=${selectedMois}`,
        { headers: getHeaders() }
      );
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Facture_${clientId}_${selectedMois}_${selectedAnnee}.pdf`;
        a.click();
      }
    } catch (error) {
      console.error('Erreur téléchargement facture:', error);
    }
  };

  // Filtrage des grilles existantes (recherche)
  const filteredGrilles = grilles.filter(grille => {
    // Filtre par nom de client
    if (searchNom) {
      const nomLower = (grille.client_nom || '').toLowerCase();
      if (!nomLower.includes(searchNom.toLowerCase())) {
        return false;
      }
    }
    
    // Filtre par activité/opération
    if (searchActivite) {
      const hasActivite = (grille.lignes || []).some(ligne => 
        (ligne.operation || '').toLowerCase().includes(searchActivite.toLowerCase())
      );
      if (!hasActivite) {
        return false;
      }
    }
    
    // Filtre par statut devis
    if (searchStatut === 'avec_devis') {
      if (!grille.devis_genere) return false;
    } else if (searchStatut === 'sans_devis') {
      if (grille.devis_genere) return false;
    }
    
    return true;
  });

  // Obtenir la liste des opérations uniques dans les grilles
  const uniqueOperations = [...new Set(
    grilles.flatMap(g => (g.lignes || []).map(l => l.operation))
  )].filter(Boolean).sort();

  // Filtrage clients (pour création)
  const filteredClients = grilleSearch.length >= 2
    ? clients.filter(c => (c.entreprise || c.nom || '').toLowerCase().includes(grilleSearch.toLowerCase())).slice(0, 100)
    : clients.slice(0, 200);

  const filteredClientsInt = intSearch.length >= 2
    ? clients.filter(c => (c.entreprise || c.nom || '').toLowerCase().includes(intSearch.toLowerCase())).slice(0, 100)
    : clients.slice(0, 200);

  // Réinitialiser les filtres
  const resetFilters = () => {
    setSearchNom('');
    setSearchActivite('');
    setSearchStatut('tous');
  };

  // Ajouter ligne tarifaire
  const addLigneTarif = () => {
    if (newOperation && newTarif) {
      setGrilleLignes([...grilleLignes, {
        operation: newOperation.toUpperCase(),
        materiel: newMateriel,
        tarif: parseFloat(newTarif),
        unite: newUnite
      }]);
      setNewOperation('');
      setNewMateriel('');
      setNewTarif('');
      setNewUnite('ha');
    }
  };

  // Créer grille
  const creerGrille = async () => {
    if (!grilleClientId || grilleLignes.length === 0) {
      alert('Veuillez sélectionner un client et ajouter au moins une ligne tarifaire');
      return;
    }
    const client = clients.find(c => c.id === grilleClientId);
    try {
      const res = await fetch(`${API_URL}/api/tarifs-eta/grilles`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          client_id: grilleClientId,
          client_nom: client?.entreprise || client?.nom || grilleClientId,
          annee: grilleAnnee,
          lignes: grilleLignes,
          notes: grilleNotes
        })
      });
      if (res.ok) {
        setShowNewGrille(false);
        setGrilleClientId('');
        setGrilleLignes([]);
        setGrilleNotes('');
        setGrilleSearch('');
        loadGrilles();
      }
    } catch (error) {
      console.error('Erreur création grille:', error);
    }
  };

  // Créer intervention
  const creerIntervention = async () => {
    if (!intClientId || !intOperation || !intHeures || !intEmployeId) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }
    const employe = equipe.find(e => e.id === intEmployeId);
    try {
      const res = await fetch(`${API_URL}/api/tarifs-eta/interventions`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          client_id: intClientId,
          parcelle_ids: intParcelleIds,
          operation: intOperation,
          materiel: intMateriel,
          date: intDate,
          heures_travaillees: parseFloat(intHeures),
          quantite_manuelle: intQuantite ? parseFloat(intQuantite) : null,
          employe_id: intEmployeId,
          employe_nom: employe ? `${employe.prenom} ${employe.nom}` : '',
          commentaire: intCommentaire
        })
      });
      if (res.ok) {
        const data = await res.json();
        alert(`Intervention enregistrée ! Montant: ${data.calcul?.montant_ht || 0}€ HT`);
        setShowNewIntervention(false);
        setIntClientId('');
        setIntSearch('');
        setIntParcelleIds([]);
        setIntOperation('');
        setIntMateriel('');
        setIntHeures('');
        setIntQuantite('');
        setIntEmployeId('');
        setIntCommentaire('');
        loadInterventions();
      }
    } catch (error) {
      console.error('Erreur création intervention:', error);
    }
  };

  // Grille du client sélectionné pour intervention
  const clientGrille = grilles.find(g => g.client_id === intClientId);

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="tarifs-eta-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Euro className="h-7 w-7 text-green-600" />
          Grilles Tarifaires & Facturation
        </h1>
        <p className="text-gray-600">Gérez vos tarifs personnalisés par client et suivez la facturation</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button variant={activeTab === 'grilles' ? 'default' : 'outline'} onClick={() => setActiveTab('grilles')}>
          <FileText className="h-4 w-4 mr-2" />Grilles Tarifaires
        </Button>
        <Button variant={activeTab === 'interventions' ? 'default' : 'outline'} onClick={() => setActiveTab('interventions')}>
          <Tractor className="h-4 w-4 mr-2" />Interventions
        </Button>
        <Button variant={activeTab === 'recap' ? 'default' : 'outline'} onClick={() => setActiveTab('recap')}>
          <Euro className="h-4 w-4 mr-2" />Récapitulatif
        </Button>
      </div>

      {/* Sélecteur période */}
      {(activeTab === 'interventions' || activeTab === 'recap') && (
        <div className="flex gap-4 items-center mb-4">
          <Calendar className="h-4 w-4 text-gray-500" />
          <select className="p-2 border rounded-lg" value={selectedMois} onChange={(e) => setSelectedMois(parseInt(e.target.value))}>
            {['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'].map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>
          <input type="number" className="w-24 p-2 border rounded-lg" value={selectedAnnee} onChange={(e) => setSelectedAnnee(parseInt(e.target.value))} />
        </div>
      )}

      {/* ============ TAB GRILLES ============ */}
      {activeTab === 'grilles' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Grilles Tarifaires {selectedAnnee}</h2>
            <Button onClick={() => setShowNewGrille(!showNewGrille)} className="bg-green-600 hover:bg-green-700">
              <Plus className="h-4 w-4 mr-2" />{showNewGrille ? 'Annuler' : 'Nouvelle Grille'}
            </Button>
          </div>

          {/* Barre de recherche des grilles */}
          <Card className="mb-4 border-blue-100 bg-blue-50/30">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 mb-3">
                <Search className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-blue-800">Rechercher dans les grilles</span>
                {(searchNom || searchActivite || searchStatut !== 'tous') && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={resetFilters}
                    className="ml-auto text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-4 w-4 mr-1" />Effacer les filtres
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Recherche par nom */}
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Par nom de client</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher un client..."
                      value={searchNom}
                      onChange={(e) => setSearchNom(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                      data-testid="search-nom"
                    />
                  </div>
                </div>
                
                {/* Recherche par activité */}
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Par activité/opération</label>
                  <select
                    value={searchActivite}
                    onChange={(e) => setSearchActivite(e.target.value)}
                    className="w-full p-2 border rounded-lg bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                    data-testid="search-activite"
                  >
                    <option value="">Toutes les activités</option>
                    {uniqueOperations.map(op => (
                      <option key={op} value={op}>{op}</option>
                    ))}
                  </select>
                </div>
                
                {/* Filtre par statut devis */}
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Statut devis</label>
                  <select
                    value={searchStatut}
                    onChange={(e) => setSearchStatut(e.target.value)}
                    className="w-full p-2 border rounded-lg bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                    data-testid="search-statut"
                  >
                    <option value="tous">Tous</option>
                    <option value="avec_devis">Avec devis généré</option>
                    <option value="sans_devis">Sans devis</option>
                  </select>
                </div>
                
                {/* Statistiques */}
                <div className="flex items-end">
                  <div className="bg-white border rounded-lg px-4 py-2 w-full text-center">
                    <span className="text-2xl font-bold text-blue-600">{filteredGrilles.length}</span>
                    <span className="text-sm text-gray-500 ml-2">/ {grilles.length} grilles</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Formulaire nouvelle grille */}
          {showNewGrille && (
            <Card className="mb-6 border-green-200 bg-green-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Plus className="h-5 w-5" />Nouvelle Grille Tarifaire</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Client / Contact / Entreprise *</label>
                    <input
                      type="text"
                      placeholder="Filtrer par nom..."
                      value={grilleSearch}
                      onChange={(e) => setGrilleSearch(e.target.value)}
                      className="w-full p-2 border rounded-lg mb-2"
                    />
                    <select
                      className="w-full p-2 border rounded-lg bg-white"
                      value={grilleClientId}
                      onChange={(e) => setGrilleClientId(e.target.value)}
                    >
                      <option value="">-- Sélectionner --</option>
                      {filteredClients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.type === 'contact' && client.entreprise && client.nom !== client.entreprise
                            ? `${client.nom} (${client.entreprise})`
                            : client.nom || client.entreprise}
                        </option>
                      ))}
                    </select>
                    {grilleClientId && <p className="text-sm text-green-600 mt-1">✓ Sélectionné</p>}
                    <p className="text-xs text-gray-500 mt-1">{clients.length} contacts/entreprises disponibles</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Année</label>
                    <input type="number" value={grilleAnnee} onChange={(e) => setGrilleAnnee(parseInt(e.target.value))} className="w-full p-2 border rounded-lg" />
                  </div>
                </div>

                {/* Ajouter ligne */}
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Ajouter une ligne tarifaire</h4>
                  <div className="grid grid-cols-5 gap-2 items-end">
                    <div className="col-span-2">
                      <label className="text-xs text-gray-500 mb-1 block">Opération * (sélectionnez ou tapez une nouvelle)</label>
                      <input 
                        placeholder="Ex: LABOUR, VENDANGE..." 
                        value={newOperation} 
                        onChange={(e) => setNewOperation(e.target.value.toUpperCase())} 
                        className="w-full p-2 border rounded-lg" 
                        list="ops-list" 
                      />
                      <datalist id="ops-list">{operations.map(op => <option key={op} value={op} />)}</datalist>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Matériel (optionnel)</label>
                      <input placeholder="Ex: Tracteur 200CV" value={newMateriel} onChange={(e) => setNewMateriel(e.target.value)} className="w-full p-2 border rounded-lg" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Tarif € *</label>
                      <input type="number" step="0.01" placeholder="Ex: 85.00" value={newTarif} onChange={(e) => setNewTarif(e.target.value)} className="w-full p-2 border rounded-lg" />
                    </div>
                    <div className="flex gap-1">
                      <div className="flex-1">
                        <label className="text-xs text-gray-500 mb-1 block">Unité *</label>
                        <select value={newUnite} onChange={(e) => setNewUnite(e.target.value)} className="w-full p-2 border rounded-lg">
                          <option value="ha">/ha</option>
                          <option value="heure">/heure</option>
                          <option value="plant">/plant</option>
                          <option value="trou">/trou</option>
                          <option value="tonne">/tonne</option>
                          <option value="jour">/jour</option>
                          <option value="m3">/m³</option>
                          <option value="km">/km</option>
                          <option value="forfait">forfait</option>
                        </select>
                      </div>
                      <div className="flex items-end">
                        <Button onClick={addLigneTarif} className="h-[42px]"><Plus className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">💡 Vous pouvez saisir une nouvelle opération qui n'existe pas encore dans la liste</p>
                </div>

                {/* Lignes ajoutées */}
                {grilleLignes.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="p-2 text-left">Opération</th>
                          <th className="p-2 text-left">Matériel</th>
                          <th className="p-2 text-right">Tarif</th>
                          <th className="p-2 text-center">Unité</th>
                          <th className="p-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {grilleLignes.map((ligne, index) => (
                          <tr key={index} className="border-t">
                            <td className="p-2 font-medium">{ligne.operation}</td>
                            <td className="p-2 text-gray-600">{ligne.materiel || '-'}</td>
                            <td className="p-2 text-right">{ligne.tarif} €</td>
                            <td className="p-2 text-center">/{ligne.unite}</td>
                            <td className="p-2 text-center">
                              <button onClick={() => setGrilleLignes(grilleLignes.filter((_, i) => i !== index))} className="text-red-500 hover:text-red-700">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium mb-1 block">Notes</label>
                  <textarea className="w-full p-2 border rounded-lg" rows={2} value={grilleNotes} onChange={(e) => setGrilleNotes(e.target.value)} placeholder="Notes ou conditions..." />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowNewGrille(false)}>Annuler</Button>
                  <Button onClick={creerGrille} className="bg-green-600 hover:bg-green-700">Créer la grille</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Liste des grilles */}
          <div className="grid gap-4">
            {filteredGrilles.map(grille => (
              <Card key={grille.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2"><Building className="h-5 w-5 text-gray-500" />{grille.client_nom}</span>
                    <div className="flex items-center gap-2">
                      {grille.devis_genere && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Devis généré</span>
                      )}
                      <span className="text-sm font-normal text-gray-500">{grille.lignes?.length || 0} tarifs • {grille.annee}</span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="p-2 text-left">Opération</th>
                          <th className="p-2 text-left">Matériel</th>
                          <th className="p-2 text-right">Tarif HT</th>
                          <th className="p-2 text-center">Unité</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grille.lignes?.map((ligne, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2 font-medium">{ligne.operation}</td>
                            <td className="p-2 text-gray-600">{ligne.materiel || '-'}</td>
                            <td className="p-2 text-right font-semibold text-green-700">{ligne.tarif} €</td>
                            <td className="p-2 text-center">/{ligne.unite}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ))}
            {filteredGrilles.length === 0 && grilles.length > 0 && (
              <Card className="p-8 text-center text-gray-500">
                <Search className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Aucune grille ne correspond à vos critères de recherche</p>
                <Button variant="outline" onClick={resetFilters} className="mt-4">
                  <X className="h-4 w-4 mr-2" />Réinitialiser les filtres
                </Button>
              </Card>
            )}
            {grilles.length === 0 && !showNewGrille && (
              <Card className="p-8 text-center text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Aucune grille tarifaire pour {selectedAnnee}</p>
                <Button onClick={() => setShowNewGrille(true)} className="mt-4">Créer ma première grille</Button>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* ============ TAB INTERVENTIONS ============ */}
      {activeTab === 'interventions' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Interventions à facturer</h2>
            <Button onClick={() => setShowNewIntervention(!showNewIntervention)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />{showNewIntervention ? 'Annuler' : 'Nouvelle Intervention'}
            </Button>
          </div>

          {/* Formulaire nouvelle intervention */}
          {showNewIntervention && (
            <Card className="mb-6 border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Tractor className="h-5 w-5" />Nouvelle Intervention</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Client *</label>
                    <input type="text" placeholder="Filtrer..." value={intSearch} onChange={(e) => setIntSearch(e.target.value)} className="w-full p-2 border rounded-lg mb-2" />
                    <select className="w-full p-2 border rounded-lg" value={intClientId} onChange={(e) => setIntClientId(e.target.value)}>
                      <option value="">-- Sélectionner --</option>
                      {filteredClientsInt.map(c => <option key={c.id} value={c.id}>{c.entreprise || c.nom}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Opération *</label>
                    <select className="w-full p-2 border rounded-lg" value={intOperation} onChange={(e) => setIntOperation(e.target.value)}>
                      <option value="">-- Sélectionner --</option>
                      {clientGrille ? clientGrille.lignes.map((l, i) => <option key={i} value={l.operation}>{l.operation} ({l.tarif}€/{l.unite})</option>) : operations.map(op => <option key={op} value={op}>{op}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Date *</label>
                    <input type="date" value={intDate} onChange={(e) => setIntDate(e.target.value)} className="w-full p-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Heures travaillées *</label>
                    <input type="number" step="0.5" placeholder="Ex: 3.5" value={intHeures} onChange={(e) => setIntHeures(e.target.value)} className="w-full p-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Salarié *</label>
                    <select className="w-full p-2 border rounded-lg" value={intEmployeId} onChange={(e) => setIntEmployeId(e.target.value)}>
                      <option value="">-- Sélectionner --</option>
                      {equipe.map(m => <option key={m.id} value={m.id}>{m.prenom} {m.nom}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Matériel</label>
                    <input placeholder="Ex: Gros Tracteur" value={intMateriel} onChange={(e) => setIntMateriel(e.target.value)} className="w-full p-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Quantité manuelle</label>
                    <input type="number" placeholder="Si plants, trous..." value={intQuantite} onChange={(e) => setIntQuantite(e.target.value)} className="w-full p-2 border rounded-lg" />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Commentaire</label>
                  <textarea className="w-full p-2 border rounded-lg" rows={2} value={intCommentaire} onChange={(e) => setIntCommentaire(e.target.value)} />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowNewIntervention(false)}>Annuler</Button>
                  <Button onClick={creerIntervention} className="bg-blue-600 hover:bg-blue-700">Enregistrer</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Liste interventions */}
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left">Date</th>
                    <th className="p-3 text-left">Client</th>
                    <th className="p-3 text-left">Opération</th>
                    <th className="p-3 text-right">Heures</th>
                    <th className="p-3 text-right">Montant HT</th>
                    <th className="p-3 text-left">Salarié</th>
                  </tr>
                </thead>
                <tbody>
                  {interventions.map(inter => (
                    <tr key={inter.id} className="border-t hover:bg-gray-50">
                      <td className="p-3">{new Date(inter.date).toLocaleDateString('fr-FR')}</td>
                      <td className="p-3 font-medium">{inter.client_nom}</td>
                      <td className="p-3">{inter.operation}</td>
                      <td className="p-3 text-right">{inter.heures_travaillees}h</td>
                      <td className="p-3 text-right font-semibold text-green-700">{inter.montant_ht}€</td>
                      <td className="p-3 text-gray-600">{inter.employe_nom}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {interventions.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <Tractor className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Aucune intervention pour cette période</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ============ TAB RECAP ============ */}
      {activeTab === 'recap' && (
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Facturation du mois</CardTitle></CardHeader>
            <CardContent>
              {recapGlobal ? (
                <>
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-700">{recapGlobal.totaux?.nb_clients || 0}</p>
                      <p className="text-sm text-gray-600">Clients</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-700">{recapGlobal.totaux?.heures || 0}h</p>
                      <p className="text-sm text-gray-600">Heures</p>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-amber-700">{recapGlobal.totaux?.montant_ht || 0}€</p>
                      <p className="text-sm text-gray-600">Total HT</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-700">{recapGlobal.totaux?.montant_ttc || 0}€</p>
                      <p className="text-sm text-gray-600">Total TTC</p>
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-3 text-left">Client</th>
                        <th className="p-3 text-right">Heures</th>
                        <th className="p-3 text-right">Interventions</th>
                        <th className="p-3 text-right">Montant HT</th>
                        <th className="p-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recapGlobal.clients?.map(client => (
                        <tr key={client.client_id} className="border-t hover:bg-gray-50">
                          <td className="p-3 font-medium">{client.client_nom}</td>
                          <td className="p-3 text-right">{client.heures}h</td>
                          <td className="p-3 text-right">{client.nb_interventions}</td>
                          <td className="p-3 text-right font-semibold text-green-700">{client.montant_ht}€</td>
                          <td className="p-3 text-center">
                            <div className="flex gap-2 justify-center">
                              <Button size="sm" variant="outline" onClick={() => loadRecapClient(client.client_id)}>Détail</Button>
                              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => downloadFacture(client.client_id)}><Download className="h-4 w-4" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <div className="text-center text-gray-500 py-8"><Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" /><p>Chargement...</p></div>
              )}
            </CardContent>
          </Card>

          {recapData && (
            <Card className="border-green-200">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Détail - {recapData.client?.entreprise || recapData.client?.nom}</span>
                  <Button onClick={() => setRecapData(null)} variant="ghost" size="sm">✕</Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm mb-4">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 text-left">Opération</th>
                      <th className="p-2 text-right">Quantité</th>
                      <th className="p-2 text-center">Unité</th>
                      <th className="p-2 text-right">Tarif</th>
                      <th className="p-2 text-right">Total HT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recapData.lignes?.map((ligne, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2 font-medium">{ligne.operation}</td>
                        <td className="p-2 text-right">{ligne.quantite_totale}</td>
                        <td className="p-2 text-center">{ligne.unite}</td>
                        <td className="p-2 text-right">{ligne.tarif_unitaire}€</td>
                        <td className="p-2 text-right font-semibold">{ligne.montant_ht}€</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-100 font-semibold">
                    <tr><td colSpan="4" className="p-2 text-right">Total HT</td><td className="p-2 text-right">{recapData.totaux?.montant_ht}€</td></tr>
                    <tr><td colSpan="4" className="p-2 text-right">TVA 20%</td><td className="p-2 text-right">{recapData.totaux?.tva_20}€</td></tr>
                    <tr className="text-lg"><td colSpan="4" className="p-2 text-right">Total TTC</td><td className="p-2 text-right text-green-700">{recapData.totaux?.montant_ttc}€</td></tr>
                  </tfoot>
                </table>
                <Button className="w-full bg-green-600 hover:bg-green-700" onClick={() => downloadFacture(recapData.client_id)}>
                  <Download className="h-4 w-4 mr-2" />Télécharger la facture PDF
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
